import { useEffect, useState } from 'react';
import type { ApiClient } from '../api/client';

/**
 * URL signées des images d'une page (ADR-103) : `GetFileDownloads` au preset demandé (spec 192 §7 :
 * la variante calculée, sinon l'original s'il s'affiche tel quel), par lots de 100, sous
 * l'autorisation de l'utilisateur. Une URL se garde jusqu'à 30 secondes avant son expiration, puis se
 * redemande ; un fichier refusé ou absent reste sans image (initiales). Aucune URL lue dans un
 * enregistrement n'est chargée : seules celles que sert cette requête le sont.
 */

export type ImageLinks = ReadonlyMap<string, string>;

const BATCH = 100;
const MARGIN_MS = 30_000;

interface Cached {
  url: string | null;
  expires_at: number;
}

/** Cache du navigateur, partagé par les pages : une même photo n'est pas redemandée à chaque vue. */
const cache = new Map<string, Cached>();

/** Pour les tests : repartir d'un cache vide. */
export function clearImageLinks(): void {
  cache.clear();
}

const FILE_ID = /^[0-9a-f]{24}$/;
/** Un fichier refusé ou absent n'est pas redemandé pendant cette durée. */
const MISSING_MS = 5 * 60_000;

function fresh(entry: Cached | undefined, now: number): entry is Cached {
  return entry !== undefined && entry.expires_at - MARGIN_MS > now;
}

function linksOf(ids: readonly string[], preset: string, now: number): Map<string, string> {
  const out = new Map<string, string>();
  for (const id of ids) {
    const entry = cache.get(`${preset}:${id}`);
    if (fresh(entry, now) && entry.url !== null) out.set(id, entry.url);
  }
  return out;
}

/** Preset des avatars (spec 192 §6.2) : 256 × 256, recadré. */
export const AVATAR_PRESET = 'avatar';

export function useImageLinks(client: ApiClient, fileIds: readonly string[], preset: string = AVATAR_PRESET): ImageLinks {
  const ids = [...new Set(fileIds.filter((id) => FILE_ID.test(id)))].sort();
  const key = `${preset}|${ids.join(',')}`;
  const [links, setLinks] = useState<ImageLinks>(() => linksOf(ids, preset, Date.now()));
  useEffect(() => {
    let alive = true;
    const now = Date.now();
    const wanted = ids.filter((id) => !fresh(cache.get(`${preset}:${id}`), now));
    setLinks(linksOf(ids, preset, now));
    if (wanted.length === 0) return;
    const batches: string[][] = [];
    for (let index = 0; index < wanted.length; index += BATCH) batches.push(wanted.slice(index, index + BATCH));
    void Promise.allSettled(
      batches.map((batch) => client.query<{ items: Array<{ file_id: string; download: { url: string; expires_at: string } }>; missing: string[] }>('GetFileDownloads', { file_ids: batch.join(','), media_type_prefix: 'image/', preset })),
    ).then((results) => {
      const at = Date.now();
      for (const result of results) {
        if (result.status !== 'fulfilled') continue;
        for (const item of result.value.data.items) {
          const expires = Date.parse(item.download.expires_at);
          cache.set(`${preset}:${item.file_id}`, { url: item.download.url, expires_at: Number.isNaN(expires) ? at : expires });
        }
        for (const id of result.value.data.missing) cache.set(`${preset}:${id}`, { url: null, expires_at: at + MISSING_MS + MARGIN_MS });
      }
      if (alive) setLinks(linksOf(ids, preset, at));
    });
    return () => {
      alive = false;
    };
    // La clé résume les identifiants : une autre page relance la lecture de ce qui manque.
  }, [client, key]);
  return links;
}
